import React, { useState } from "react";
import {
  Combobox,
  useCombobox,
  InputBase,
  Group,
  Text,
  Divider,
  ActionIcon,
} from "@mantine/core";
import { IconX, IconMessage } from "@tabler/icons-react";
import { useClient } from "../../context/ClientContext";
import { formatPhoneNumberDisplay, toE164 } from "../../utils/phoneUtils";

/**
 * Recipient picker for starting a chat. Reuses the same client search as
 * `ClientLookup` (via `useClient`), but never forces choosing a client: when the
 * typed text is a complete phone number it ALWAYS offers a bottom
 * "Text (xxx) xxx-xxxx and don't associate a client" option.
 *
 * Emits the chosen recipient through `onChange`:
 *   - a client:     { phoneE164, clientId }
 *   - a raw number: { phoneE164 }            (no clientId)
 *   - cleared:      null
 * The caller decides what to do with it (e.g. create/open the conversation).
 *
 * No visible label by default (pass `label` to add one); `ariaLabel` keeps the
 * field accessible to screen readers when there is no visible label. The field
 * is not marked `required` — callers enforce their own submit rules (e.g. New
 * Chat disables "Start chat" until a recipient is chosen).
 *
 * @param {object} props
 * @param {(recipient: {phoneE164: string, clientId?: string} | null) => void} props.onChange
 * @param {string} [props.error]
 * @param {string} [props.label]
 * @param {string} [props.ariaLabel]
 * @param {boolean} [props.disabled]
 */
function ClientOrPhoneLookup({
  onChange,
  error,
  label,
  ariaLabel = "Recipient",
  disabled = false,
}) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const { clients, searchQuery, setSearchQuery } = useClient();
  const [selectedLabel, setSelectedLabel] = useState("");

  // The typed query as a dialable US number, or "" when it isn't one yet.
  const typedE164 = toE164(searchQuery);

  const clientPhoneE164 = (client) =>
    client.area_code && client.phone_number
      ? toE164(`${client.area_code}${client.phone_number}`)
      : "";

  const clientDisplay = (client) => {
    const name = `${client.first_name || ""} ${client.last_name || ""}`.trim();
    const phone = clientPhoneE164(client);
    return [name, phone ? formatPhoneNumberDisplay(phone) : ""]
      .filter(Boolean)
      .join(" — ");
  };

  const handleSubmit = (val) => {
    if (disabled) return;
    if (val === "__phone__") {
      setSelectedLabel(`Text ${formatPhoneNumberDisplay(typedE164)}`);
      onChange({ phoneE164: typedE164 });
    } else {
      const client = clients.find((c) => String(c.id) === val);
      if (!client) return;
      setSelectedLabel(clientDisplay(client));
      onChange({
        phoneE164: clientPhoneE164(client),
        clientId: String(client.id),
      });
    }
    setSearchQuery("");
    combobox.closeDropdown();
  };

  const handleClear = (e) => {
    if (disabled) return;
    e.stopPropagation();
    setSelectedLabel("");
    setSearchQuery("");
    onChange(null);
  };

  const displayValue = selectedLabel || searchQuery;

  return (
    <Combobox
      store={combobox}
      disabled={disabled}
      onOptionSubmit={handleSubmit}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          aria-label={label || ariaLabel}
          error={error}
          disabled={disabled}
          placeholder="Search a client or type a phone number"
          value={displayValue}
          rightSection={
            selectedLabel ? (
              <ActionIcon
                size="sm"
                variant="subtle"
                aria-label="Clear recipient"
                onClick={handleClear}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : (
              <Combobox.Chevron />
            )
          }
          onFocus={() => !disabled && combobox.openDropdown()}
          onClick={() => !disabled && combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          onChange={(e) => {
            if (disabled) return;
            if (selectedLabel) {
              setSelectedLabel("");
              onChange(null);
            }
            setSearchQuery(e.currentTarget.value);
            combobox.openDropdown();
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options mah={280} style={{ overflowY: "auto" }}>
          {clients.map((client) => (
            <Combobox.Option value={String(client.id)} key={client.id}>
              <Text size="sm">
                {client.first_name} {client.last_name}
              </Text>
              <Text size="xs" c="dimmed">
                {clientPhoneE164(client)
                  ? formatPhoneNumberDisplay(clientPhoneE164(client))
                  : ""}
                {client.email ? ` | ${client.email}` : ""}
              </Text>
            </Combobox.Option>
          ))}

          {!searchQuery && clients.length === 0 && (
            <Combobox.Empty>
              Search a client by name or phone, or type a number to text.
            </Combobox.Empty>
          )}
          {searchQuery && clients.length === 0 && !typedE164 && (
            <Combobox.Empty>
              No clients found — type a full phone number to text the number
              directly.
            </Combobox.Empty>
          )}

          {typedE164 && (
            <>
              {clients.length > 0 && <Divider my={4} />}
              <Combobox.Option value="__phone__">
                <Group gap="xs" wrap="nowrap">
                  <IconMessage size={16} />
                  <Text size="sm">
                    Text {formatPhoneNumberDisplay(typedE164)} and don&apos;t
                    associate a client
                  </Text>
                </Group>
              </Combobox.Option>
            </>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

export default ClientOrPhoneLookup;
